import React, { useContext } from "react";
import { useFragment } from "react-relay";

import { graphql } from "relay-runtime";

import useThrottledValue from "~/lib/hooks/useThrottledValue";
import MeetingPhaseContext from "~/stream/MeetingPhaseContext";

import CopilotStatus from "./CopilotStatus";
import InMeetingControls from "./InMeetingControls";
import StartAndAutoRecordingControls from "./StartAndAutoRecordingControls";

import type { RecordingControls_note$key } from "~/__generated__/RecordingControls_note.graphql";
import type { RecordingControls_viewer$key } from "~/__generated__/RecordingControls_viewer.graphql";

import styles from "./RecordingControls.module.css";

interface RecordingControlsProps {
	viewer: RecordingControls_viewer$key;
	note: RecordingControls_note$key;
}

function RecordingControls({ viewer: viewerKey, note: noteKey }: RecordingControlsProps) {
	const { noMeeting, copilotStatus: _copilotStatus, stopRecording } = useContext(MeetingPhaseContext);
	const copilotStatus = useThrottledValue(_copilotStatus, 500);

	const viewer = useFragment(
		graphql`
			fragment RecordingControls_viewer on Viewer {
				hasAccessToCopilot
				...StartAndAutoRecordingControls_viewer
			}
		`,
		viewerKey,
	);

	const note = useFragment(
		graphql`
			fragment RecordingControls_note on Note {
				...StartAndAutoRecordingControls_note
			}
		`,
		noteKey,
	);

	if (noMeeting || !viewer.hasAccessToCopilot) return null;

	switch (copilotStatus.type) {
		case "not-recording":
		case "scheduled": {
			return <StartAndAutoRecordingControls viewer={viewer} note={note} />;
		}
		case "recording":
		case "paused": {
			return <InMeetingControls copilotStatus={copilotStatus} />;
		}
		default: {
			return <CopilotStatus copilotStatus={copilotStatus} onCancelClicked={stopRecording} />;
		}
	}
}

export default function RecordControlsHiddenDuringPrint(props: RecordingControlsProps) {
	return (
		<div className={styles.hiddenDuringPrint}>
			<RecordingControls {...props} />
		</div>
	);
}
